import { useState, useCallback } from "react";
import { MapPin, Navigation, Plus, X, Loader2, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/helpers";
import LocationAutocomplete from "./LocationAutocomplete";

const POPULAR_MARKETS = [
  "Tampa, FL", "Miami, FL", "Orlando, FL",
  "Houston, TX", "Dallas, TX", "San Antonio, TX",
  "Atlanta, GA", "Charlotte, NC", "Nashville, TN",
  "Phoenix, AZ", "Las Vegas, NV", "Jacksonville, FL",
  "Columbus, OH", "Indianapolis, IN", "Memphis, TN",
];

const MarketSelectionModal = ({ isOpen, onClose, onMarketsSelected, allowSkip = false }) => {
  const [markets, setMarkets] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [geoLoading, setGeoLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const addMarket = useCallback((market) => {
    const trimmed = market.trim();
    if (!trimmed) return;
    if (markets.some((m) => m.toLowerCase() === trimmed.toLowerCase())) {
      toast.info("Market already added");
      return;
    }
    setMarkets((prev) => [...prev, trimmed]);
    setInputValue("");
  }, [markets]);

  const removeMarket = (market) => {
    setMarkets((prev) => prev.filter((m) => m !== market));
  };

  const handleUseCurrentLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setGeoLoading(true);
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000,
        });
      });

      const { latitude, longitude } = position.coords;

      // Reverse geocode using our backend
      const resp = await axios.get(`${API}/geocode/reverse`, {
        params: { lat: latitude, lng: longitude },
      });

      if (resp.data?.location) {
        addMarket(resp.data.location);
        toast.success(`Detected: ${resp.data.location}`);
      } else {
        toast.error("Could not determine your location. Please enter it manually.");
      }
    } catch (err) {
      if (err.code === 1) {
        toast.error("Location access denied. Please enter your market manually.");
      } else if (err.code === 3) {
        toast.error("Location request timed out. Please enter your market manually.");
      } else {
        toast.error("Could not detect location. Please enter your market manually.");
      }
    } finally {
      setGeoLoading(false);
    }
  };

  const handleSave = async () => {
    if (markets.length === 0) {
      toast.error("Please add at least one market");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/user/markets`, { markets });
      onMarketsSelected(markets);
    } catch (err) {
      toast.error("Failed to save markets");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={allowSkip ? onClose : undefined}>
      <DialogContent
        className={`sm:max-w-[520px] w-[95vw] p-0 bg-card border-border overflow-hidden ${!allowSkip ? '[&>button]:hidden' : ''}`}
        data-testid="market-selection-modal"
        onPointerDownOutside={(e) => { if (!allowSkip) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (!allowSkip) e.preventDefault(); }}
      >
        {/* Header accent bar */}
        <DialogTitle className="sr-only">Choose Your Markets</DialogTitle>
        <div className="h-1.5 bg-gradient-to-r from-emerald-500 via-emerald-400 to-teal-500" />

        <div className="px-6 pt-5 pb-6">
          {/* Title */}
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground" data-testid="market-modal-title">
                Choose Your Markets
              </h2>
              <p className="text-xs text-muted-foreground">
                Search properties in your preferred locations
              </p>
            </div>
          </div>

          {/* Use Current Location button */}
          <button
            onClick={handleUseCurrentLocation}
            disabled={geoLoading}
            className="w-full mt-5 flex items-center gap-3 p-3.5 rounded-lg border border-dashed border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 transition-colors text-left group"
            data-testid="use-current-location-btn"
          >
            {geoLoading ? (
              <Loader2 className="w-5 h-5 text-emerald-500 animate-spin flex-shrink-0" />
            ) : (
              <Navigation className="w-5 h-5 text-emerald-500 flex-shrink-0 group-hover:scale-110 transition-transform" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">
                {geoLoading ? "Detecting location..." : "Use Current Location"}
              </p>
              <p className="text-[11px] text-muted-foreground">
                Auto-detect your city and state
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-emerald-500 transition-colors flex-shrink-0" />
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-border" />
            <span className="text-[10px] text-muted-foreground uppercase tracking-widest">or search manually</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Manual input */}
          <div className="flex gap-2">
            <LocationAutocomplete
              value={inputValue}
              onChange={setInputValue}
              onSelect={(display) => {
                addMarket(display);
              }}
              placeholder="Enter city, state, or zip code..."
              className="flex-1"
              data-testid="market-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => addMarket(inputValue)}
              disabled={!inputValue.trim()}
              className="px-3"
              data-testid="market-add-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          {/* Selected markets */}
          {markets.length > 0 && (
            <div className="mt-4" data-testid="selected-markets">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">
                Your Markets ({markets.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {markets.map((market) => (
                  <Badge
                    key={market}
                    className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 pr-1.5 flex items-center gap-1"
                    data-testid={`market-tag-${market.replace(/[,\s]+/g, "-")}`}
                  >
                    <MapPin className="w-3 h-3" />
                    {market}
                    <button
                      onClick={() => removeMarket(market)}
                      className="ml-1 hover:bg-red-500/20 rounded-full p-0.5 transition-colors"
                      data-testid={`remove-market-${market.replace(/[,\s]+/g, "-")}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Popular markets */}
          <div className="mt-4">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">
              Popular Markets
            </p>
            <div className="flex flex-wrap gap-1.5">
              {POPULAR_MARKETS.map((city) => {
                const isSelected = markets.some(
                  (m) => m.toLowerCase() === city.toLowerCase()
                );
                return (
                  <button
                    key={city}
                    onClick={() => !isSelected && addMarket(city)}
                    disabled={isSelected}
                    className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                      isSelected
                        ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-500 cursor-default"
                        : "bg-secondary/30 border-border text-muted-foreground hover:text-foreground hover:border-emerald-500/30"
                    }`}
                    data-testid={`popular-market-${city.replace(/[,\s]+/g, "-")}`}
                  >
                    {city}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
            {allowSkip ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-muted-foreground"
                data-testid="market-skip-btn"
              >
                Skip for now
              </Button>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                Select at least one market to continue
              </p>
            )}
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white min-w-[140px]"
              onClick={handleSave}
              disabled={markets.length === 0 || saving}
              data-testid="market-save-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : null}
              {saving ? "Saving..." : `Search ${markets.length > 0 ? markets.length : ""} Market${markets.length !== 1 ? "s" : ""}`}
            </Button>
          </div>

          {/* Privacy note */}
          <p className="text-[10px] text-muted-foreground mt-3 text-center">
            Location data is used only to recommend local markets.{" "}
            <a href="/legal/privacy" className="text-emerald-500 hover:underline" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MarketSelectionModal;
